<template>
  <div :class="alertClasses" v-if="isOpen">
    <div :class="['alert__content', `alert__content--${color}`]">
      <div class="alert__content__text">
        <slot name="default"></slot>
      </div>
      <button type="button" :class="['alert__content__icon', `alert__content__icon--${color}`]" @click="onClose">
        <Icon name="close" size="sm" color="white"/>
      </button>
    </div>
  </div>
</template>

<script>
import Icon from '../Icon/Icon.vue'
export default {
  name: 'Alert',
  status: 'prototype',
  release: '1.0.0',
  components: { Icon },
  props: {
    value: {
      type: Boolean,
      default: true
    },
    position: {
      type: String,
      default: null,
      validator: value => {
        return value.match(/(null|top|topLeft|topRight|bottom|bottomLeft|bottomRight)/)
      }
    },
    text: {
      type: String,
      default: null
    },
    color: {
      type: String,
      default: 'gray'
    },
    textColor: {
      type: String,
      default: '#FFFFFF'
    },
    persist: {
      type: Boolean,
      default: false
    },
    durationTime: {
      type: String,
      default: '10'
    }
  },
  mounted () {
    if (this.value) {
      this.startTimeout()
    }
  },
  data () {
    return {
      '$timeout': null,
      lazyValue: this.value
    }
  },
  watch: {
    value (val) {
      if (val) {
        this.startTimeout()
      }
      this.lazyValue = val
    }
  },
  computed: {
    alertClasses () {
      const classes = ['alert']

      switch (this.position) {
        case 'top':
          classes.push('alert--top')
          break
        case 'topLeft':
          classes.push('alert--top-left')
          break
        case 'topRight':
          classes.push('alert--top-right')
          break
        case 'bottom':
          classes.push('alert--bottom')
          break
        case 'bottomLeft':
          classes.push('alert--bottom-left')
          break
        case 'bottomRight':
          classes.push('alert--bottom-right')
          break
      }

      return classes
    },
    isOpen: {
      get () {
        return this.lazyValue
      },
      set (val) {
        this.lazyValue = val
        this.$emit('input', val)
      }
    }
  },
  methods: {
    onClose () {
      this.isOpen = false
      this.clearTimeout()
      this.$emit('close')
    },
    startTimeout () {
      this.clearTimeout()
      if (!this.persist) {
        this.$timeout = setTimeout(() => this.onClose(), Number(this.durationTime) * 1000)
      }
    },
    clearTimeout () {
      if (this.$timeout) {
        clearTimeout(this.$timeout)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/styles.scss";

.alert {
  z-index: 9999;
  &--top {
    position: fixed;
    top: 20px;
    left: 20px;
    right: 20px;
  }
  &--top-left {
    position: fixed;
    top: 20px;
    left: 20px;
  }
  &--top-right {
    position: fixed;
    top: 20px;
    right: 20px;
  }
  &--bottom {
    position: fixed;
    bottom: 20px;
    left: 20px;
    right: 20px;
  }
  &--bottom-left {
    position: fixed;
    bottom: 20px;
    left: 20px;
  }
  &--bottom-right {
    position: fixed;
    bottom: 20px;
    right: 20px;
  }
  &__content {
    @include reset();
    position: relative;
    padding: 18px 50px 18px 30px;
    &--purple-lighter {
      background-color: $color-purple-lighter;
    }
    &--purple-light {
      background-color: $color-purple-light;
    }
    &--purple {
      background-color: $color-purple;
    }
    &--purple-dark {
      background-color: $color-purple-dark;
    }
    &--purple-darker {
      background-color: $color-purple-darker;
    }
    &--gray-light {
      background-color: $color-gray-light;
    }
    &--gray {
      background-color: $color-gray;
    }
    &--gray-dark {
      background-color: $color-gray-dark;
    }
    &--yellow-lighter {
      background-color: $color-yellow-lighter;
    }
    &--yellow-light {
      background-color: $color-yellow-light;
    }
    &--yellow {
      background-color: $color-yellow;
    }
    &--green {
      background-color: $color-green;
    }
    &--pink-lighter {
      background-color: $color-pink-lighter;
    }
    &--pink-light {
      background-color: $color-pink-light;
    }
    &--pink {
      background-color: $color-pink;
    }
    &--white {
      background-color: $color-white;
    }
    &--rich-black {
      background-color: $color-rich-black;
    }

    &__text {
      font-family: $font-text;
      font-size: $size-xs;
      font-weight: 600;
      font-style: normal;
      font-stretch: normal;
      line-height: normal;
      letter-spacing: normal;
      color: rgb(255, 255, 255);

      strong {
        font-weight: 800;
      }

      a {
        color: inherit;
        text-decoration: underline;
      }
    }
    &__icon {
      position: absolute;
      width: auto;
      height: auto;
      padding: 0;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      cursor: pointer;
      background: transparent;
      border: 0;

      &:active,
      &:focus {
        outline: none;
      }

      &--purple-lighter {
        .b-icon {
          &:after {
            color: $color-purple-lighter !important;
          }
        }
      }
      &--purple-light {
        .b-icon {
          &:after {
            color: $color-purple-light !important;
          }
        }
      }
      &--purple {
        .b-icon {
          &:after {
            color: $color-purple !important;
          }
        }
      }
      &--purple-dark {
        .b-icon {
          &:after {
            color: $color-purple-dark !important;
          }
        }
      }
      &--purple-darker {
        .b-icon {
          &:after {
            color: $color-purple-darker !important;
          }
        }
      }
      &--gray-light {
        .b-icon {
          &:after {
            color: $color-gray-light !important;
          }
        }
      }
      &--gray {
        .b-icon {
          &:after {
            color: $color-gray !important;
          }
        }
      }
      &--gray-dark {
        .b-icon {
          &:after {
            color: $color-gray-dark !important;
          }
        }
      }
      &--yellow-lighter {
        .b-icon {
          &:after {
            color: $color-yellow-lighter !important;
          }
        }
      }
      &--yellow-light {
        .b-icon {
          &:after {
            color: $color-yellow-light !important;
          }
        }
      }
      &--yellow {
        .b-icon {
          &:after {
            color: $color-yellow !important;
          }
        }
      }
      &--green {
        .b-icon {
          &:after {
            color: $color-green !important;
          }
        }
      }
      &--pink-lighter {
        .b-icon {
          &:after {
            color: $color-pink-lighter !important;
          }
        }
      }
      &--pink-light {
        .b-icon {
          &:after {
            color: $color-pink-light !important;
          }
        }
      }
      &--pink {
        .b-icon {
          &:after {
            color: $color-pink !important;
          }
        }
      }
      &--white {
        .b-icon {
          &:after {
            color: $color-white !important;
          }
        }
      }
      &--rich-black {
        .b-icon {
          &:after {
            color: $color-rich-black !important;
          }
        }
      }
    }
  }
}
</style>
